import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid';
import { ContactStatus, IContactParams } from 'shared';

import { useFetchContactsQuery } from '../services/api/contactsApi/contacts';
import { RootState } from '../store';
import { initialState, setParams } from '../slices/contactParams';
import {
  reset,
  setFilterModel,
  setSortModel,
  setPaginationModel,
  resetFilter,
  resetSort,
  resetPagination,
} from '../slices/contactTableModels';
import { parseCurrencyFilter } from '../lib/currencyHelpers';
import { numberFilterParams } from '../lib/datagrid';

export enum ContactTableFields {
  name = `name`,
  paidniceGroup = `paidniceGroup`,
  frozen = `frozen`,
  externalAggregateNames = `externalAggregateNames`,
  outstandingBalance = `outstandingBalance`,
  overdueBalance = `overdueBalance`,
  unpaidInvoiceCount = `unpaidInvoiceCount`,
  oldestDaysOverdue = `oldestDaysOverdue`,
  isJob = `isJob`,
  isProject = `isProject`,
  assignee = `assignee`,
  // Spanned Rows
  email = `email`,
  mobileNumber = `mobileNumber`,
  includeInNotifications = `includeInNotifications`,
  contactPersonName = `contactPersonName`,
  avgDaysToPay = `avgDaysToPay`,
  creditScore = `creditScore`,
  totalRevenueLast12Months = `totalRevenueLast12Months`,
  daysSinceLastCall = `daysSinceLastCall`,
  daysSinceLastText = `daysSinceLastText`,
  daysSinceLastPayment = `daysSinceLastPayment`,
  latestEmailEvent = `latestEmailEvent`,

  // Hidden cols
  externalAccountNumber = `externalAccountNumber`,
  status = `status`,
  tags = `tags`,
  personTags = `personTags`,
  groupId = `groupId`,
  portalUrl = `portalUrl`,
  externalId = `externalId`,
  parentContactName = `parentContactName`,
  hasActivePaymentPlan = `hasActivePaymentPlan`,
}

export function useContacts() {
  const { organisationId, groupId: groupIdParam } = useParams<{ organisationId: string; groupId?: string }>();
  const { params } = useContactParams();

  const groupId = groupIdParam ? Number(groupIdParam) : undefined;

  const result = useFetchContactsQuery({
    ...params,
    // If groupId is present in URL, then override the params, otherwise the group ID params takes precedence, it may be defined or null
    groupId: groupId ? groupId : params.groupId,
    organisationId,
  }, {
    skip: !organisationId,
    refetchOnMountOrArgChange: true,
    // pollingInterval:  1000 * 15, UNCOMMENT THIS LINE -----------------------------------------------------------------------------------------------
    skipPollingIfUnfocused: true,
  });

  return result;
}

export function useContactParams() {
  const params = useSelector((s: RootState) => s.contactParams);
  const dispatch = useDispatch();

  const updateParams = (update: Partial<typeof params>) => {
    return dispatch(setParams(update));
  };

  const resetParams = (keepKeys?: string[]) => {
    const newState = { ...initialState };

    if (keepKeys) {
      keepKeys.forEach(key => {
        newState[key] = params[key];
      });
    }

    dispatch(setParams(newState));
  };

  return {
    resetParams,
    updateParams,
    params,
  };
}

export function useContactTableModels() {
  const dispatch = useDispatch();
  const contactTableModels = useSelector((s: RootState) => s.contactTableModels);

  return {
    // State
    filterModel: contactTableModels.filterModel,
    sortModel: contactTableModels.sortModel,
    paginationModel: contactTableModels.paginationModel,
    // Dispatch
    setFilterModel: (filterModel: GridFilterModel) => dispatch(setFilterModel(filterModel)),
    setSortModel: (sortModel: GridSortModel) => dispatch(setSortModel(sortModel)),
    setPaginationModel: (paginationModel: GridPaginationModel) => dispatch(setPaginationModel(paginationModel)),
    resetModels: () => dispatch(reset()),
    resetSort: () => dispatch(resetSort()),
    resetFilter: () => dispatch(resetFilter()),
    resetPagination: () => dispatch(resetPagination()),
  };
}

/** Builds the API params for the contact table from the current grid filter/sort/pagination state */
export function useContactTableParams(): IContactParams {
  const { filterModel, sortModel, paginationModel } = useContactTableModels();
  const { groupId } = useParams<{ groupId?: string }>();

  const quickSearchStr = filterModel.quickFilterValues?.[0];

  const tags = filterModel.items.find(f => f.field === ContactTableFields.tags)?.value;
  const personTags = filterModel.items.find(f => f.field === ContactTableFields.personTags)?.value;

  const params: IContactParams = {
    limit: paginationModel.pageSize,
    page: paginationModel.page + 1, // MUI is 0 based

    status: filterModel.items.find(f => f.field === ContactTableFields.status)?.value as ContactStatus | undefined,
    frozen: filterModel.items.find(f => f.field === ContactTableFields.frozen)?.value,
    groupId: filterModel.items.find(f => f.field === ContactTableFields.paidniceGroup)?.value,
    groupIdOperator: filterModel.items.find(f => f.field === ContactTableFields.paidniceGroup)?.operator as `is` | `not` | undefined,
    searchStr: quickSearchStr,
    externalAggregateName: filterModel.items.find(f => f.field === ContactTableFields.externalAggregateNames)?.value,
    tags: tags ? [tags] : undefined,
    personTags: personTags ? [personTags] : undefined,
    name: filterModel.items.find(f => f.field === ContactTableFields.name)?.value,
    personName: filterModel.items.find(f => f.field === ContactTableFields.contactPersonName)?.value,
    email: filterModel.items.find(f => f.field === ContactTableFields.email)?.value,
    emailOperator: filterModel.items.find(f => f.field === ContactTableFields.email)?.operator as `contains` | `isEmpty` | undefined,
    mobile: filterModel.items.find(f => f.field === ContactTableFields.mobileNumber)?.value,
    mobileOperator: filterModel.items.find(f => f.field === ContactTableFields.mobileNumber)?.operator as `contains` | `isEmpty` | `isInvalid` | undefined,
    externalAccountId: filterModel.items.find(f => f.field === ContactTableFields.externalAccountNumber)?.value,
    ...numberFilterParams(filterModel.items, ContactTableFields.outstandingBalance, `outstandingBalance`, parseCurrencyFilter),
    ...numberFilterParams(filterModel.items, ContactTableFields.overdueBalance, `overdueBalance`, parseCurrencyFilter),
    ...numberFilterParams(filterModel.items, ContactTableFields.unpaidInvoiceCount, `unpaidInvoiceCount`),
    ...numberFilterParams(filterModel.items, ContactTableFields.oldestDaysOverdue, `oldestDaysOverdue`),
    isJob: filterModel.items.find(f => f.field === ContactTableFields.isJob)?.value,
    isProject: filterModel.items.find(f => f.field === ContactTableFields.isProject)?.value,
    hasActivePaymentPlan: filterModel.items.find(f => f.field === ContactTableFields.hasActivePaymentPlan)?.value,
    ...numberFilterParams(filterModel.items, ContactTableFields.avgDaysToPay, `avgDaysToPay`),
    ...numberFilterParams(filterModel.items, ContactTableFields.creditScore, `creditScore`),
    ...numberFilterParams(filterModel.items, ContactTableFields.totalRevenueLast12Months, `totalRevenueLast12Months`, parseCurrencyFilter),
    ...numberFilterParams(filterModel.items, ContactTableFields.daysSinceLastCall, `daysSinceLastCall`),
    ...numberFilterParams(filterModel.items, ContactTableFields.daysSinceLastText, `daysSinceLastText`),
    ...numberFilterParams(filterModel.items, ContactTableFields.daysSinceLastPayment, `daysSinceLastPayment`),
    latestEmailEvents: filterModel.items.find(f => f.field === ContactTableFields.latestEmailEvent)?.value as string[] | undefined,
    assignedUserId: filterModel.items.find(f => f.field === ContactTableFields.assignee)?.value as string | undefined,

    // Sort
    sortColumn: sortModel[0]?.field,
    sortDirection: sortModel[0]?.sort as `asc` | `desc` | undefined,
  };

  if (groupId) {
    params.groupId = Number(groupId);
    params.groupIdOperator = undefined;
  }

  return params;
}

export function useContactsV2() {
  const params = useContactTableParams();
  const { organisationId } = useParams<{ organisationId?: string }>();

  const result = useFetchContactsQuery({
    ...params,
    organisationId,
  }, {
    skip: !organisationId,
    refetchOnMountOrArgChange: true,
    pollingInterval:  1000 * 15,
    skipPollingIfUnfocused: true,
  });

  if (!result.data) {
    result.data = {
      contacts: [],
      totalCount: 0,
      totalVersionOneCount: 0,
    };
  }

  return result;
}
